// Trigger Cascade hook debug
import { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Dashboard } from './pages/Dashboard';
import { Default } from './pages/Default';
import { Pricing } from './pages/Pricing';
import { About } from './pages/about';
import Layout from './components/Layout';
import { SymbolLayout } from './components/SymbolLayout';
import { ThemeProvider } from './components/ThemeContext';
import { StockInvestmentSimulator } from './pages/StockInvestmentSimulator';
import { Provider } from 'react-redux';
import { store } from './store';
import Login from './pages/Login';
import ProtectedRoute from './helper/ProtectedRoute';
import { UserProvider } from './context/UserContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Signup from './pages/Signup';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import OrientationEnforcer from './components/OrientationEnforcer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { Home } from './pages/Home';
import Screener from './pages/Screener';
import News from './pages/News';
import { StockComparisonPage } from './pages/StockComparisonPage';
import MarketMoversPage from './pages/MarketMoversPage';
import Sitemap from './pages/Sitemap';
import Earnings from './pages/Earnings';
import ETFs from './pages/ETFs';
import SuperInvestors from './pages/SuperInvestors';
import InsiderTrades from './pages/InsiderTrades';
import SenateTrades from './pages/SenateTrades';
import ETFDetail from './pages/ETFDetail';
import Institutions from './pages/Institutions';
import InstitutionPortfolio from './pages/InstitutionPortfolio';
import HallOfFame from './pages/HallOfFame';
import HallOfFameSubmit from './pages/HallOfFameSubmit';
import HallOfFameLeaderboard from './pages/HallOfFameLeaderboard';
import StockDirectory from './pages/StockDirectory';
import Analytics from './services/analytics';

import { AnalyticsTracker } from './components/AnalyticsTracker';
import { InvestmentGoalCalculator } from './pages/InvestmentGoalCalculator';

export function App() {
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [isSignUp, setIsSignUp] = useState<boolean>(false);

  // Initialize Analytics on mount
  useEffect(() => {
    Analytics.init();
  }, []);

  const openPopup = (signUpMode: boolean = false): void => {
    setIsSignUp(signUpMode);
    setIsPopupOpen(true);
  };
  const closePopup = (): void => setIsPopupOpen(false);

  // Determine whether to enable the orientation overlay. Defaults to DISABLED
  // Enable only when explicitly requested via env, URL, or localStorage.
  const envFlag = (import.meta.env.VITE_ENABLE_ORIENTATION_ENFORCER ?? 'false').toString().toLowerCase();
  const envEnabled = envFlag === 'true' || envFlag === '1' || envFlag === 'on';
  let queryEnabled = false;
  try {
    const params = new URLSearchParams(window.location.search);
    const orientationParam = (params.get('orientation') || '').toLowerCase();
    const enableParam = (params.get('enable-orientation') || '').toLowerCase();
    queryEnabled = ['1', 'true', 'on'].includes(orientationParam) || ['1', 'true', 'on'].includes(enableParam);
  } catch {
    // ignore
  }
  let storageEnabled = false;
  try {
    storageEnabled = localStorage.getItem('enableOrientationEnforcer') === 'true';
  } catch {
    // ignore
  }
  const enableOrientationOverlay = envEnabled || queryEnabled || storageEnabled;

  // Referral handling: capture from URL on first load and persist to localStorage.
  const [referral, setReferral] = useState<string | null>(() => {
    try {
      return localStorage.getItem('referralCode');
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const candidates = ['ref', 'referral', 'r', 'utm_ref', 'referral_code'];
      let code: string | null = null;

      for (const name of candidates) {
        const v = params.get(name);
        if (v) { code = v; break; }
      }

      // also check hash (e.g. #ref=abc)
      if (!code && window.location.hash) {
        const hash = window.location.hash.replace(/^#/, '');
        const hParams = new URLSearchParams(hash);
        for (const name of candidates) {
          const v = hParams.get(name);
          if (v) { code = v; break; }
        }
      }
      console.log('Referral code from URL:', code);

      if (code) {
        setReferral(code);
        try { localStorage.setItem('referralCode', code); } catch { /* ignore */ }
      }

    } catch {
      // ignore parsing errors
    }
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider>
        <UserProvider>
          <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_GOOGLE_CLIENT_ID || ""}>
            <BrowserRouter>
              <AnalyticsTracker />
              {enableOrientationOverlay && <OrientationEnforcer />}
              <Layout openPopup={openPopup} isPopupOpen={isPopupOpen} isSignUp={isSignUp} closePopup={closePopup} referral={referral}>
                <Routes>
                  <Route path="/" element={<Default />} />
                  <Route path="/chart-analysis" element={<Home />} />
                  <Route path="/pricing" element={<Pricing openPopup={openPopup} />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path='/login' element={<Login />} />
                  <Route path='/signup' element={<Signup />} />
                  <Route path="/news" element={<News />} />
                  <Route path="/earnings" element={<Earnings />} />
                  <Route path="/etfs" element={<ETFs />} />
                  <Route path="/etfs/:symbol" element={<ETFDetail />} />
                  <Route path="/fund-insights" element={<SuperInvestors />} />
                  <Route path="/super-investors" element={<SuperInvestors />} />
                  <Route path="/insider-trades" element={<InsiderTrades />} />
                  <Route path="/senate-trades" element={<SenateTrades />} />
                  <Route path="/institutions" element={<Institutions />} />
                  <Route path="/institution/:cik" element={<InstitutionPortfolio />} />
                  <Route path="/:symbol/:section" element={<SymbolLayout />} />
                  <Route path="/:symbol/:section/:subsection" element={<SymbolLayout />} />
                  <Route path="/:symbol/:section/:subsection/:child" element={<SymbolLayout />} />
                  <Route path="/compare" element={<StockComparisonPage />} />
                  <Route path="/simulator" element={<StockInvestmentSimulator openPopup={openPopup} />} />
                  <Route path="/screener" element={<Screener />} />
                  <Route path="/market-movers" element={<MarketMoversPage />} />
                  <Route path="/hall-of-fame" element={<HallOfFame />} />
                  <Route path="/hall-of-fame/submit" element={<HallOfFameSubmit />} />
                  <Route path="/hall-of-fame/leaderboard" element={<HallOfFameLeaderboard />} />
                  <Route path="/sitemap" element={<Sitemap openPopup={openPopup} />} />
                  <Route path="/investment-goal" element={<InvestmentGoalCalculator openPopup={openPopup} />} />
                  {/* Dashboard now public (temporarily ungated) */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/stocks/directory" element={<StockDirectory />} />
                  <Route element={<ProtectedRoute openPopup={openPopup} closePopup={closePopup} />}>
                    <Route path="/subscription/success" element={<Subscription />} />
                    <Route path="/profile" element={<Profile />} />
                    {/* keep symbol advanced routes gated */}
                    <Route path="/:symbol/:section" element={<SymbolLayout />} />
                    {/* <Route path="/:symbol/:section/" element={<SymbolLayout />} /> */}
                  </Route>
                </Routes>
              </Layout>
            </BrowserRouter>
          </GoogleOAuthProvider>
        </UserProvider>
      </ThemeProvider>
    </Provider>
  );
}


